import type { ReminderItem } from '../../../hooks/reminder-mapping'
import type { StagedAction } from '../../../hooks/staged-actions/types'

/** One of a meeting's action items: an action planner v2 staged, or a reminder it wrote. */
export type MeetingActionItem =
  | { kind: 'action'; id: string; createdAt: number; action: StagedAction }
  | { kind: 'reminder'; id: string; createdAt: number; reminder: ReminderItem }

/** An open reminder the meeting raised: noted with its recording, or pointing at its saved note. */
export function isMeetingReminder(
  reminder: ReminderItem,
  audioSessionId: string | undefined,
  audioNoteId: string | undefined,
): boolean {
  if (reminder.state === 'dismissed' || reminder.resolved) {
    return false
  }
  return (
    (!!audioSessionId && reminder.audioSessionId === audioSessionId) ||
    (!!audioNoteId && reminder.source?.type === 'audioNote' && reminder.source.id === audioNoteId)
  )
}

export function actionItemOf(action: StagedAction): MeetingActionItem {
  return { kind: 'action', id: action.id, createdAt: Date.parse(action.createdAt), action }
}

/** A meeting's actions and reminders as one list, oldest first. */
export function mergeMeetingActionItems(
  actions: Array<StagedAction>,
  reminders: Array<ReminderItem>,
): Array<MeetingActionItem> {
  return [
    ...actions.map(actionItemOf),
    ...reminders.map(
      (reminder): MeetingActionItem => ({
        kind: 'reminder',
        id: reminder.id,
        createdAt: reminder.createdAt.getTime(),
        reminder,
      }),
    ),
  ].sort((a, b) => a.createdAt - b.createdAt)
}
